import React, { FC, useCallback, useMemo } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import TreeItem from '@material-ui/lab/TreeItem';
import useStyles from 'components/TreeViewSingle/style';
import { ITreeViewItem } from 'components/TreeViewSingle/types';

const TreeViewItem: FC<ITreeViewItem> = ({
  node,
  level = 0,
  onLabelClick = () => {},
  selected,
  renderLabel,
  dynamicChildren,
  loadChildren = () => {},
}) => {
  const { children = [], hasChildren } = node;
  const currentLevel = level + 1;

  const classes = useStyles({ level: currentLevel, selected: node.value === selected });

  const onItemLabelCLick = useCallback(
    (event: { preventDefault: () => void }) => {
      onLabelClick(node.value);
      event.preventDefault();
    },
    [node.value, onLabelClick]
  );

  const shouldLoadChildren =
    dynamicChildren && typeof hasChildren !== 'boolean' && children.length === 0;

  const onIconClick = () => {
    if (shouldLoadChildren) {
      loadChildren(node);
    }
  };

  const emptyChildrenNode = useMemo(
    () =>
      shouldLoadChildren ? (
        <Box py={2} display="flex" justifyContent="center" borderBottom="1px solid #E5E5E5">
          <CircularProgress size={20} />{' '}
        </Box>
      ) : null,
    [shouldLoadChildren]
  );

  const renderChildren = useMemo(
    () =>
      children.length > 0
        ? children.map((node) => (
            <TreeViewItem
              key={`tree-item-${node.value}`}
              node={node}
              level={currentLevel}
              onLabelClick={onLabelClick}
              selected={selected}
              renderLabel={renderLabel}
              dynamicChildren={dynamicChildren}
              loadChildren={loadChildren}
            />
          ))
        : emptyChildrenNode,

    [
      children,
      currentLevel,
      dynamicChildren,
      emptyChildrenNode,
      loadChildren,
      onLabelClick,
      renderLabel,
      selected,
    ]
  );

  return (
    <TreeItem
      nodeId={node.value}
      label={renderLabel ? renderLabel(node) : node.name}
      classes={classes}
      onLabelClick={onItemLabelCLick}
      data-testid={`tree-item-${node.value}`}
      onIconClick={onIconClick}
    >
      {renderChildren}
    </TreeItem>
  );
};

export default TreeViewItem;
